import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Header } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import {
  Plus, Trash2, Calendar, ChevronLeft, ChevronRight, Clock, Users,
  Loader2, Settings2, Link2, CheckCircle, Edit, Send, RefreshCw,
} from "lucide-react";
import { useData } from "../contexts/DataContext";
import { useAuth } from "../contexts/AuthContext";
import { fmtTime12 } from "../lib/time";
import TimePicker12 from "../components/TimePicker12";
import DatePickerField from "../components/DatePickerField";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_COLORS = {
  scheduled: "bg-blue-100 text-blue-700",
  in_progress: "bg-amber-100 text-amber-700",
  completed: "bg-emerald-100 text-emerald-700",
  needs_reschedule: "bg-purple-100 text-purple-700",
  cancelled: "bg-red-100 text-red-700",
};

const DEFAULT_COLORS = [
  "#0F4C81", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444", "#06B6D4", "#EC4899", "#F97316",
];

const DEFAULT_TYPE_FORM = {
  name: "",
  duration_hours: 2,
  color: "#0F4C81",
  description: "",
  default_start_time: "08:00",
  is_install_event: false,
  notify_on_create: true,
  notify_reminder: true,
  reminder_minutes_before: 120,
  email_subject_create: "",
  email_body_create: "",
  email_subject_reminder: "",
  email_body_reminder: "",
  notify_crew_on_create: false,
  crew_email: "",
  crew_email_subject: "",
  crew_email_body: "",
  target_service_type: "",
  target_stage: "",
};

const DEFAULT_CREATE_SUBJECT = "Your {{event_title}} is scheduled with {{company_name}}";
const DEFAULT_CREATE_BODY = `<p>Hi {{customer_name}},</p>
<p>This confirms that your <b>{{event_title}}</b> has been scheduled.</p>
<p><b>Date:</b> {{event_date}}<br>
<b>Time:</b> {{event_time}}<br>
<b>Location:</b> {{project_address}}</p>
<p>If you need to reschedule, just reply to this email.</p>
<p>Thanks,<br>{{company_name}}</p>`;

const DEFAULT_REMINDER_SUBJECT = "Reminder: Your {{event_title}} is coming up";
const DEFAULT_REMINDER_BODY = `<p>Hi {{customer_name}},</p>
<p>Friendly reminder — your <b>{{event_title}}</b> with {{company_name}} is coming up soon.</p>
<p><b>Date:</b> {{event_date}}<br>
<b>Time:</b> {{event_time}}<br>
<b>Location:</b> {{project_address}}</p>
<p>Reply to this email if anything has changed.</p>
<p>See you soon,<br>{{company_name}}</p>`;

export default function Schedule() {
  const { projects, fetchProjects, contacts, pipelineStages, pipelineCategories, fetchPipelineStages, fetchPipelineCategories } = useData();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [teamUsers, setTeamUsers] = useState([]);
  const [filterAssignee, setFilterAssignee] = useState(""); // "" = all, "unassigned" = none, else user id
  const [filterTypeId, setFilterTypeId] = useState(""); // "" = all types
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editingType, setEditingType] = useState(null);
  const [calendarStatus, setCalendarStatus] = useState({ connected: false, email: "" });
  const [pullingFromGoogle, setPullingFromGoogle] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay() + 1);
    d.setHours(0, 0, 0, 0);
    return d;
  });

  // View mode: "week" (default original grid) or "month" (full-month calendar)
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem("schedule_view_mode") || "week";
    } catch { return "week"; }
  });
  // Anchor date for the visible month (first day of month)
  const [currentMonthDate, setCurrentMonthDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  });

  const [formData, setFormData] = useState({
    project_id: "", contact_id: "", title: "", event_type_id: "", crew: "", assigned_to: "",
    start_date: new Date().toISOString().split("T")[0],
    end_date: "", start_time: "08:00", end_time: "17:00",
    status: "scheduled", notes: "", color: "#0F4C81",
  });

  // Once we know who is signed in, prefill the "Assigned To" field for
  // brand-new events. Only touches the field while it's still empty and
  // no event is being edited — never overwrites a manual choice or a
  // saved event's existing assignee.
  useEffect(() => {
    if (user?.id && !editingEvent && !formData.assigned_to) {
      setFormData(prev => ({ ...prev, assigned_to: user.id }));
    }
  }, [user?.id, editingEvent, formData.assigned_to]);

  const [typeForm, setTypeForm] = useState({ ...DEFAULT_TYPE_FORM });

  // Tracks whether the user has manually edited end_time in the current
  // modal session. Once true, changing start_time won't auto-slide end.
  // Reset whenever the modal opens/closes or the event type changes.
  const endManuallyEditedRef = useRef(false);

  // Time math helpers (HH:MM string ↔ minutes since midnight)
  const _toMins = (t) => {
    if (!t || typeof t !== "string") return 0;
    const [h, m] = t.split(":").map(n => parseInt(n, 10) || 0);
    return h * 60 + m;
  };
  const _fromMins = (mins) => {
    const m = Math.max(0, Math.min(mins, 24 * 60 - 1));
    return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
  };

  /**
   * Auto-slide end_time when the user changes start_time.
   *  - Default gap: 30 min (per user request)
   *  - If the current gap is already set and bigger, preserve it
   *  - Skip entirely for production events (full-day jobs)
   *  - Skip if user has manually edited end_time this session
   */
  const handleStartTimeChange = (newStart) => {
    setFormData(prev => {
      const et = eventTypes.find(t => t.id === prev.event_type_id);
      const isProduction = et?.target_service_type === "production";
      if (isProduction || endManuallyEditedRef.current) {
        return { ...prev, start_time: newStart };
      }
      const oldStartMins = _toMins(prev.start_time);
      const oldEndMins = _toMins(prev.end_time);
      const currentGap = oldEndMins > oldStartMins ? oldEndMins - oldStartMins : 30;
      const gap = Math.max(currentGap, 30);
      return {
        ...prev,
        start_time: newStart,
        end_time: _fromMins(_toMins(newStart) + gap),
      };
    });
  };

  const handleEndTimeChange = (newEnd) => {
    endManuallyEditedRef.current = true;
    setFormData(prev => ({ ...prev, end_time: newEnd }));
  };

  // Check Google Calendar connection status on mount
  useEffect(() => {
    const gcalResult = searchParams.get("gcal");
    if (gcalResult === "success") {
      toast.success("Google Calendar connected!");
      window.history.replaceState({}, "", "/schedule");
    }
    if (gcalResult === "error") {
      toast.error("Failed to connect Google Calendar");
      window.history.replaceState({}, "", "/schedule");
    }

    if (searchParams.get("create") === "true") setShowModal(true);

    fetchCalendarStatus();
    fetchEventTypes();
    fetchTeamUsers();
    fetchProjects();
    fetchPipelineStages();
    fetchPipelineCategories();
  }, [fetchProjects, fetchPipelineStages, fetchPipelineCategories, searchParams]);

  const fetchTeamUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/users`);
      setTeamUsers((res.data || []).filter(u => u.role !== "viewer"));
    } catch { /* may lack permission */ }
  };

  useEffect(() => { fetchEvents(); }, [currentWeekStart, currentMonthDate, viewMode]);

  const fetchCalendarStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/oauth/calendar/status`);
      setCalendarStatus(res.data);
    } catch {}
  };

  const fetchEventTypes = async () => {
    try {
      const res = await axios.get(`${API_URL}/event-types`);
      setEventTypes(res.data);
    } catch {}
  };

  const fetchEvents = async () => {
    let start, end;
    if (viewMode === "month") {
      // Include leading/trailing days of adjacent months shown in the 6-row grid
      const first = new Date(currentMonthDate);
      first.setDate(1);
      const gridStart = new Date(first);
      gridStart.setDate(1 - ((first.getDay() + 6) % 7)); // Monday-anchored
      const gridEnd = new Date(gridStart);
      gridEnd.setDate(gridStart.getDate() + 41); // 6 weeks × 7 days − 1
      start = gridStart.toISOString().split("T")[0];
      end = gridEnd.toISOString().split("T")[0];
    } else {
      start = currentWeekStart.toISOString().split("T")[0];
      end = new Date(currentWeekStart.getTime() + 6 * 86400000).toISOString().split("T")[0];
    }
    try {
      const res = await axios.get(`${API_URL}/schedule?start_date=${start}&end_date=${end}`);
      setEvents(res.data);
    } catch {}
    setLoading(false);
  };

  const handleConnectGoogle = async () => {
    setConnecting(true);
    try {
      const res = await axios.get(`${API_URL}/oauth/calendar/connect`);
      window.location.href = res.data.auth_url;
    } catch (err) {
      toast.error("Failed to start Google connection");
      setConnecting(false);
    }
  };

  const handleDisconnectGoogle = async () => {
    try {
      await axios.delete(`${API_URL}/oauth/calendar/disconnect`);
      setCalendarStatus({ connected: false, email: "" });
      toast.success("Google Calendar disconnected");    } catch { toast.error("Failed to disconnect"); }
  };

  const handlePullFromGoogle = async () => {
    setPullingFromGoogle(true);
    try {
      const res = await axios.post(`${API_URL}/calendar/pull-from-google`);
      const n = res.data?.events_updated ?? 0;
      toast.success(n > 0 ? `Synced ${n} event${n === 1 ? "" : "s"} from Google` : "Already up to date");
      // Refresh the list so any changes show immediately
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    } finally {
      setPullingFromGoogle(false);
    }
  };

  // When user selects an event type, auto-fill duration and update title
  const handleEventTypeChange = (typeId) => {
    const et = eventTypes.find(t => t.id === typeId);
    if (et) {
      // Picking a type re-asserts a duration → clear any manual end-time override
      endManuallyEditedRef.current = false;
      const startTime = et.default_start_time || "08:00";
      const [h, m] = startTime.split(":").map(Number);
      const endH = h + Math.floor(et.duration_hours);
      const endM = m + Math.round((et.duration_hours % 1) * 60);
      const endTime = `${String(Math.min(endH + Math.floor(endM / 60), 23)).padStart(2, "0")}:${String(endM % 60).padStart(2, "0")}`;

      // Build title: "EventType - ContactName - Address"
      let title = et.name;
      const proj = projects.find(p => p.id === formData.project_id);
      if (proj) {
        const contact = contacts.find(c => c.id === proj.contact_id);
        const parts = [et.name];
        if (contact?.name) parts.push(contact.name);
        if (proj.project_address) parts.push(proj.project_address);
        title = parts.join(" - ");
      }

      setFormData(prev => ({
        ...prev,
        event_type_id: typeId,
        title,
        color: et.color || prev.color,
        start_time: startTime,
        end_time: endTime,
      }));
    } else {
      setFormData(prev => ({ ...prev, event_type_id: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEvent) {
        const res = await axios.put(`${API_URL}/schedule/${editingEvent.id}`, formData);
        setEvents(prev => prev.map(ev => ev.id === editingEvent.id ? res.data : ev));
        toast.success("Event updated!");
      } else {
        const res = await axios.post(`${API_URL}/schedule`, formData);
        setEvents(prev => [...prev, res.data]);
        toast.success(calendarStatus.connected ? "Event created & synced to Google Calendar!" : "Event created!");
      }
      closeModal();
    } catch { toast.error("Failed to save event"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await axios.delete(`${API_URL}/schedule/${id}`);
      setEvents(prev => prev.filter(e => e.id !== id));
      toast.success("Moved to trash");
    } catch { toast.error("Failed to delete"); }
  };

  const handleStatusChange = async (id, status) => {
    try {
      const res = await axios.patch(`${API_URL}/schedule/${id}/status?status=${status}`);
      setEvents(prev => prev.map(e => e.id === id ? res.data : e));
    } catch { toast.error("Failed to update status"); }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingEvent(null);
    endManuallyEditedRef.current = false;
    setFormData({
      project_id: "", contact_id: "", title: "", event_type_id: "", crew: "",
      assigned_to: user?.id || "",
      start_date: new Date().toISOString().split("T")[0],
      end_date: "", start_time: "08:00", end_time: "17:00",
      status: "scheduled", notes: "", color: "#0F4C81",
    });
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    // Treat an existing event's end_time as "manually set" so editing the
    // start time doesn't silently truncate the saved duration.
    endManuallyEditedRef.current = true;
    setFormData({ ...event });
    setShowModal(true);
  };

  const handleSaveType = async () => {
    if (!typeForm.name) { toast.error("Name required"); return; }
    try {
      if (editingType) {
        const res = await axios.put(`${API_URL}/event-types/${editingType.id}`, typeForm);
        setEventTypes(prev => prev.map(t => t.id === editingType.id ? res.data : t));
        toast.success("Event type updated!");
      } else {
        const res = await axios.post(`${API_URL}/event-types`, typeForm);
        setEventTypes(prev => [...prev, res.data]);
        toast.success("Event type created!");
      }
      setEditingType(null);
      setTypeForm({ ...DEFAULT_TYPE_FORM });
      setShowTypeModal(false);
    } catch { toast.error("Failed to save event type"); }
  };

  const handleDeleteType = async (id) => {
    try {
      await axios.delete(`${API_URL}/event-types/${id}`);
      setEventTypes(prev => prev.filter(t => t.id !== id));
      toast.success("Event type deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const [sendingTest, setSendingTest] = useState(""); // "" | "create" | "reminder"
  const handleSendTestEmail = async (kind) => {
    if (!typeForm.name) { toast.error("Add a name first"); return; }
    setSendingTest(kind);
    try {
      // If we have a saved id, use it so the endpoint can fall back to saved values
      const id = editingType?.id || "new";
      const res = await axios.post(`${API_URL}/event-types/${id}/test-email`, {
        kind,
        draft: typeForm,
      });
      toast.success(`Test sent to ${res.data.to}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send test email");
    } finally {
      setSendingTest("");
    }
  };

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(currentWeekStart);
    d.setDate(d.getDate() + i);
    return d;
  });

  const visibleEvents = events.filter(e => {
    if (filterTypeId && e.event_type_id !== filterTypeId) return false;
    if (!filterAssignee) return true;
    if (filterAssignee === "unassigned") return !e.assigned_to;
    return e.assigned_to === filterAssignee;
  });

  const getEventsForDay = (date) => {
    const dateStr = date.toISOString().split("T")[0];
    return visibleEvents.filter(e => {
      const start = e.start_date;
      const end = e.end_date || e.start_date;
      return dateStr >= start && dateStr <= end;
    });
  };

  const prevWeek = () => setCurrentWeekStart(new Date(currentWeekStart.getTime() - 7 * 86400000));
  const nextWeek = () => setCurrentWeekStart(new Date(currentWeekStart.getTime() + 7 * 86400000));
  const goToday = () => {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay() + 1);
    d.setHours(0, 0, 0, 0);
    setCurrentWeekStart(d);
    const m = new Date();
    m.setDate(1);
    m.setHours(0, 0, 0, 0);
    setCurrentMonthDate(m);
  };
  const prevMonth = () => {
    const d = new Date(currentMonthDate);
    d.setMonth(d.getMonth() - 1);
    setCurrentMonthDate(d);
  };
  const nextMonth = () => {
    const d = new Date(currentMonthDate);
    d.setMonth(d.getMonth() + 1);
    setCurrentMonthDate(d);
  };
  const setView = (mode) => {
    setViewMode(mode);
    try { localStorage.setItem("schedule_view_mode", mode); } catch {}
  };
  const isToday = (date) => date.toDateString() === new Date().toDateString();

  // Build the 42-cell (6-week) Monday-anchored grid for the current month.
  const monthGridDays = (() => {
    const first = new Date(currentMonthDate);
    first.setDate(1);
    const start = new Date(first);
    start.setDate(1 - ((first.getDay() + 6) % 7));
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  })();

  return (
    <div className="min-h-screen">
      <Header title="Production Schedule">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Google Calendar Status */}
          {calendarStatus.connected ? (
            <>
              <div className="flex items-center gap-1.5 pl-2 pr-1 py-1 bg-emerald-50 border border-emerald-200 rounded-lg text-xs sm:text-sm" data-testid="gcal-connected">
                <CheckCircle className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                <span className="text-emerald-700 truncate max-w-[110px] sm:max-w-[160px]" title={calendarStatus.email}>{calendarStatus.email}</span>
                <button
                  type="button"
                  onClick={() => { if (window.confirm(`Disconnect Google Calendar (${calendarStatus.email})? Events stay in Tides Hub; only the sync stops.`)) handleDisconnectGoogle(); }}
                  className="ml-1 px-2 py-1 rounded-md text-[11px] font-semibold text-emerald-700 hover:bg-red-50 hover:text-red-600 transition-colors"
                  data-testid="disconnect-gcal-btn"
                >
                  Disconnect
                </button>
              </div>
              <Button
                variant="outline"
                className="h-9 text-xs sm:text-sm"
                onClick={handlePullFromGoogle}
                disabled={pullingFromGoogle}
                title="Pull latest changes from Google Calendar now"
                data-testid="pull-from-google-btn"
              >
                <RefreshCw className={`w-4 h-4 sm:mr-1 ${pullingFromGoogle ? "animate-spin" : ""}`} />
                <span className="hidden sm:inline">Sync from Google</span>
              </Button>
            </>
          ) : (
            <Button variant="outline" className="h-9 text-xs sm:text-sm" onClick={handleConnectGoogle} disabled={connecting} data-testid="connect-gcal-btn">
              {connecting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Calendar className="w-4 h-4 mr-1" />}
              <span className="hidden sm:inline">Connect Google Calendar</span>
              <span className="sm:hidden">Google Cal</span>
            </Button>
          )}

          <Button variant="outline" className="h-9 text-xs sm:text-sm" onClick={() => setShowTypeModal(true)} data-testid="manage-event-types-btn">
            <Settings2 className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Event Types</span>
          </Button>

          {/* View mode toggle: Week / Month */}
          <div className="flex items-center bg-slate-100 rounded-lg p-0.5" data-testid="schedule-view-toggle">
            <button
              onClick={() => setView("week")}
              className={`h-8 px-3 text-xs sm:text-sm rounded-md font-medium transition-colors ${viewMode === "week" ? "bg-white text-[#0F4C81] shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
              data-testid="schedule-view-week"
            >
              Week
            </button>
            <button
              onClick={() => setView("month")}
              className={`h-8 px-3 text-xs sm:text-sm rounded-md font-medium transition-colors ${viewMode === "month" ? "bg-white text-[#0F4C81] shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
              data-testid="schedule-view-month"
            >
              Month
            </button>
          </div>

          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={viewMode === "month" ? prevMonth : prevWeek} data-testid="schedule-prev"><ChevronLeft className="w-4 h-4" /></Button>
            <Button variant="outline" className="h-9 px-2 text-xs sm:text-sm" onClick={goToday} data-testid="schedule-today">Today</Button>
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={viewMode === "month" ? nextMonth : nextWeek} data-testid="schedule-next"><ChevronRight className="w-4 h-4" /></Button>
          </div>

          <Button className="bg-[#0F4C81] hover:bg-[#0D4270] h-9 px-3 sm:px-5 text-xs sm:text-sm" onClick={() => setShowModal(true)} data-testid="create-schedule-btn">
            <Plus className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">New Event</span>
          </Button>
        </div>
      </Header>

      <div className="p-3 sm:p-6">
        {/* Range header + assignee filter */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
          <h2 className="text-sm sm:text-lg font-semibold text-slate-700 text-center sm:text-left" data-testid="schedule-range-label">
            {viewMode === "month"
              ? currentMonthDate.toLocaleDateString("en-US", { month: "long", year: "numeric" })
              : `${currentWeekStart.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${weekDays[6].toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
            }
          </h2>
          <div className="flex items-center gap-2 flex-wrap">
            <Label htmlFor="assignee-filter" className="text-xs text-slate-500 whitespace-nowrap">Who:</Label>
            <select
              id="assignee-filter"
              value={filterAssignee}
              onChange={(e) => setFilterAssignee(e.target.value)}
              className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F4C81]/20 min-w-[160px]"
              data-testid="schedule-assignee-filter"
            >
              <option value="">Everyone ({events.length})</option>
              <option value="unassigned">— Unassigned —</option>
              {teamUsers.map(u => {
                const count = events.filter(e => e.assigned_to === u.id).length;
                return (
                  <option key={u.id} value={u.id}>
                    {u.name || u.email} ({count})
                  </option>
                );
              })}
            </select>
            <Label htmlFor="type-filter" className="text-xs text-slate-500 whitespace-nowrap">Type:</Label>
            <select
              id="type-filter"
              value={filterTypeId}
              onChange={(e) => setFilterTypeId(e.target.value)}
              className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F4C81]/20 min-w-[160px]"
              data-testid="schedule-type-filter"
            >
              <option value="">All types ({events.length})</option>
              {eventTypes.map(t => {
                const count = events.filter(e => e.event_type_id === t.id).length;
                return (
                  <option key={t.id} value={t.id}>
                    {t.name} ({count})
                  </option>
                );
              })}
            </select>
          </div>
        </div>

        {/* Color legend — only show if there are event types configured */}
        {eventTypes.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap mb-3 pb-2 border-b border-slate-100" data-testid="schedule-type-legend">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mr-1">Legend</span>
            {eventTypes.map(t => {
              const active = filterTypeId === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setFilterTypeId(active ? "" : t.id)}
                  className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-medium transition-all ${active ? "ring-2 ring-offset-1" : "hover:bg-slate-100"}`}
                  style={{
                    backgroundColor: t.color + "18",
                    color: t.color,
                    ...(active ? { boxShadow: `0 0 0 2px ${t.color}55` } : {}),
                  }}
                  data-testid={`legend-chip-${t.id}`}
                >
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: t.color }} />
                  {t.name}
                </button>
              );
            })}
            {filterTypeId && (
              <button
                type="button"
                onClick={() => setFilterTypeId("")}
                className="text-[11px] text-slate-500 hover:text-slate-700 underline underline-offset-2 ml-1"
                data-testid="legend-clear"
              >
                Clear filter
              </button>
            )}
          </div>
        )}

        {/* ============ MONTH GRID ============ */}
        {viewMode === "month" && (
          <div data-testid="schedule-month-grid">
            {/* Weekday header */}
            <div className="grid grid-cols-7 gap-px sm:gap-1 mb-1">
              {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d) => (
                <div key={d} className="text-center text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-slate-400 py-1">
                  {d}
                </div>
              ))}
            </div>
            {/* 6-row day grid */}
            <div className="grid grid-cols-7 gap-px sm:gap-1 bg-slate-100 rounded-lg overflow-hidden">
              {monthGridDays.map((day, i) => {
                const inMonth = day.getMonth() === currentMonthDate.getMonth();
                const dayEvents = getEventsForDay(day);
                return (
                  <div
                    key={i}
                    className={`min-h-[80px] sm:min-h-[120px] p-1 sm:p-2 flex flex-col ${inMonth ? "bg-white" : "bg-slate-50"}`}
                    data-testid={`month-day-${day.toISOString().split("T")[0]}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span
                        className={`text-xs sm:text-sm font-semibold ${
                          isToday(day)
                            ? "bg-[#0F4C81] text-white w-6 h-6 rounded-full flex items-center justify-center"
                            : inMonth ? "text-slate-700" : "text-slate-300"
                        }`}
                      >
                        {day.getDate()}
                      </span>
                      {dayEvents.length > 3 && (
                        <span className="text-[9px] text-slate-400 font-medium">
                          +{dayEvents.length - 3}
                        </span>
                      )}
                    </div>
                    <div className="space-y-0.5 flex-1 overflow-hidden">
                      {dayEvents.slice(0, 3).map(event => (
                        <div
                          key={event.id}
                          className="px-1 sm:px-1.5 py-0.5 rounded text-[9px] sm:text-[11px] cursor-pointer hover:opacity-80 truncate leading-tight"
                          style={{ backgroundColor: event.color + "22", borderLeft: `2px solid ${event.color}`, color: event.color }}
                          onClick={() => handleEdit(event)}
                          data-testid={`month-event-${event.id}`}
                          title={`${event.title} · ${fmtTime12(event.start_time)}`}
                        >
                          <span className="hidden sm:inline">{fmtTime12(event.start_time)} </span>
                          {event.title}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ============ WEEK GRID ============ */}
        {viewMode === "week" && (
        <div className="grid grid-cols-7 gap-px sm:gap-2">
          {weekDays.map((day, i) => (
            <div key={i} className="min-h-[100px] sm:min-h-[200px]">
              <div className={`text-center py-1 sm:py-2 rounded-t-lg text-xs sm:text-sm font-medium ${isToday(day) ? "bg-[#0F4C81] text-white" : "bg-slate-100 text-slate-600"}`}>
                <div className="hidden sm:block">{day.toLocaleDateString("en-US", { weekday: "short" })}</div>
                <div className="sm:hidden">{day.toLocaleDateString("en-US", { weekday: "narrow" })}</div>
                <div className="text-sm sm:text-lg font-bold">{day.getDate()}</div>
              </div>
              <div className="border border-t-0 border-slate-200 rounded-b-lg p-0.5 sm:p-1 space-y-0.5 sm:space-y-1 min-h-[70px] sm:min-h-[160px] bg-white">
                {getEventsForDay(day).map(event => (
                  <div
                    key={event.id}
                    className="p-1 sm:p-2 rounded text-[10px] sm:text-xs cursor-pointer hover:opacity-80 transition-opacity"
                    style={{ backgroundColor: event.color + "20", borderLeft: `2px solid ${event.color}` }}
                    onClick={() => handleEdit(event)}
                    data-testid={`schedule-event-${event.id}`}
                  >
                    <p className="font-medium truncate" style={{ color: event.color }}>{event.title}</p>
                    <p className="text-slate-400 hidden sm:block">{fmtTime12(event.start_time)} - {fmtTime12(event.end_time)}</p>
                    <p className="text-slate-400 sm:hidden">{fmtTime12(event.start_time)}</p>
                    <div className="hidden sm:flex items-center gap-1 mt-1">
                      <Badge className={`${STATUS_COLORS[event.status]} text-[10px]`}>{event.status.replace("_", " ")}</Badge>
                      {event.google_event_id && <Calendar className="w-3 h-3 text-blue-400" title="Synced to Google Calendar" />}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        )}

        {/* Event List */}
        {visibleEvents.length > 0 && (
          <Card className="mt-4 sm:mt-6">
            <CardHeader className="pb-2 sm:pb-4"><CardTitle className="text-sm sm:text-base">{viewMode === "month" ? "This Month's Events" : "This Week's Events"}</CardTitle></CardHeader>
            <CardContent className="px-3 sm:px-6">
              <div className="space-y-2 sm:space-y-3">
                {visibleEvents.map(event => {
                  const project = projects.find(p => p.id === event.project_id);
                  const evType = eventTypes.find(t => t.id === event.event_type_id);
                  const assignee = teamUsers.find(u => u.id === event.assigned_to);
                  return (
                    <div key={event.id} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-3 sm:p-4 rounded-lg border border-slate-200 group">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-1 h-10 sm:h-12 rounded-full flex-shrink-0" style={{ backgroundColor: event.color }} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-sm text-slate-700 truncate">{event.title}</p>
                            {evType && <Badge variant="outline" className="text-[10px] sm:text-xs">{evType.name}</Badge>}
                            {assignee && (
                              <Badge variant="outline" className="text-[10px] sm:text-xs bg-blue-50 border-blue-200 text-[#0F4C81]">
                                {assignee.name || assignee.email}
                              </Badge>
                            )}
                            {event.google_event_id && <Calendar className="w-3 h-3 text-blue-400 flex-shrink-0" title="On Google Calendar" />}
                          </div>
                          <div className="flex flex-wrap gap-2 sm:gap-3 text-xs sm:text-sm text-slate-500 mt-0.5">
                            <span><Clock className="w-3 h-3 inline mr-0.5" />{fmtTime12(event.start_time)} - {fmtTime12(event.end_time)}</span>
                            <span><Calendar className="w-3 h-3 inline mr-0.5" />{event.start_date}</span>
                            {project && <span className="text-slate-400 truncate">{project.project_address}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4 sm:ml-0">
                        <Select value={event.status} onValueChange={(v) => handleStatusChange(event.id, v)}>
                          <SelectTrigger className="w-28 sm:w-36 h-8 sm:h-9 text-xs sm:text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="scheduled">Scheduled</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="needs_reschedule">Needs Reschedule</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button variant="ghost" size="sm" className="text-slate-500 hover:text-[#0F4C81] hover:bg-blue-50 h-8 w-8 p-0" onClick={() => handleEdit(event)} data-testid={`edit-event-${event.id}`} title="Edit event">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0" onClick={() => handleDelete(event.id)} data-testid={`delete-event-${event.id}`} title="Delete event">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* New/Edit Event Modal */}
      <Dialog open={showModal} onOpenChange={(open) => { if (!open) closeModal(); else setShowModal(true); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEvent ? "Edit Event" : "New Event"}</DialogTitle>
            <DialogDescription>
              {calendarStatus.connected ? "Will sync to Google Calendar" : "Schedule crew work and visits"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            {/* Event Type Selector */}
            {eventTypes.length > 0 && (
              <div className="space-y-2">
                <Label>Event Type</Label>
                <div className="flex flex-wrap gap-2">
                  {eventTypes.map(et => (
                    <button
                      key={et.id}
                      type="button"
                      className={`px-3 py-1.5 rounded-full text-sm font-medium border-2 transition-all ${
                        formData.event_type_id === et.id
                          ? "border-[#0F4C81] bg-[#0F4C81]/10 text-[#0F4C81]"
                          : "border-slate-200 text-slate-600 hover:border-slate-300"
                      }`}
                      onClick={() => handleEventTypeChange(et.id)}
                      data-testid={`event-type-btn-${et.id}`}
                    >
                      <span className="w-2 h-2 rounded-full inline-block mr-1.5" style={{ backgroundColor: et.color }} />
                      {et.name} ({et.duration_hours >= 1 ? `${et.duration_hours}h` : `${Math.round(et.duration_hours * 60)}m`})
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Title *</Label>
              <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="e.g., Roof Visit - 123 Main St" className="h-12" required data-testid="schedule-title-input" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Project</Label>
                <Select value={formData.project_id || "none"} onValueChange={(v) => {
                  const pid = v === "none" ? "" : v;
                  const proj = projects.find(p => p.id === pid);
                  const contact = proj ? contacts.find(c => c.id === proj.contact_id) : null;
                  const et = eventTypes.find(t => t.id === formData.event_type_id);
                  let autoTitle = formData.title;
                  if (proj) {
                    const parts = [];
                    if (et?.name) parts.push(et.name);
                    if (contact?.name) parts.push(contact.name);
                    if (proj.project_address) parts.push(proj.project_address);
                    autoTitle = parts.join(" - ");
                  }
                  setFormData({ ...formData, project_id: pid, title: autoTitle });
                }}>
                  <SelectTrigger className="h-12"><SelectValue placeholder="Link to project" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {projects.map(p => {
                      const c = contacts.find(ct => ct.id === p.contact_id);
                      return <SelectItem key={p.id} value={p.id}>{c ? `${c.name} - ` : ""}{p.project_address || "No address"}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Crew Name</Label>
                <Input value={formData.crew} onChange={(e) => setFormData({ ...formData, crew: e.target.value })} placeholder="e.g., Crew A" className="h-12" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Assign To Team Member</Label>
              <select
                value={formData.assigned_to || ""}
                onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                className="w-full h-12 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F4C81]/20"
                data-testid="schedule-assign-to-select"
              >
                <option value="">— Unassigned —</option>
                {teamUsers.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.name || u.email}{u.role ? ` (${u.role})` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                {/* Native date input — guaranteed reliable across mobile browsers
                    (unlike our Popover-based picker which has intermittent
                    open-failures on iOS Safari inside a scrollable Dialog). */}
                <Input
                  type="date"
                  value={formData.start_date || ""}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="h-12"
                  data-testid="event-start-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={formData.end_date || ""}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="h-12"
                  data-testid="event-end-date-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time</Label>
                <TimePicker12 value={formData.start_time} onChange={handleStartTimeChange} testIdPrefix="event-start" />
              </div>
              <div className="space-y-2">
                <Label>End Time</Label>
                <TimePicker12 value={formData.end_time} onChange={handleEndTimeChange} testIdPrefix="event-end" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex gap-2">
                {DEFAULT_COLORS.map(c => (
                  <button key={c} type="button" className={`w-7 h-7 rounded-full border-2 transition-all ${formData.color === c ? "border-slate-800 scale-110" : "border-transparent"}`} style={{ backgroundColor: c }} onClick={() => setFormData({ ...formData, color: c })} />
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Optional notes" rows={3} />
            </div>

            <div className="flex gap-3 pt-2">
              {editingEvent && (
                <Button type="button" variant="outline" className="h-12 px-4 text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600" onClick={() => { handleDelete(editingEvent.id); closeModal(); }} data-testid="delete-event-modal-btn">
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
              <Button type="button" variant="outline" className="flex-1 h-12" onClick={closeModal}>Cancel</Button>
              <Button type="submit" className="flex-1 h-12 bg-[#0F4C81] hover:bg-[#0D4270]" data-testid="submit-schedule-btn">
                {editingEvent ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Event Types Modal */}
      <Dialog open={showTypeModal} onOpenChange={setShowTypeModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Event Types</DialogTitle>
            <DialogDescription>Pre-configure event types with default durations</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Existing types */}
            {eventTypes.length > 0 && (
              <div className="space-y-2">
                {eventTypes.map(et => (
                  <div key={et.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg" data-testid={`event-type-${et.id}`}>
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: et.color }} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-slate-700">{et.name}</p>
                      <p className="text-xs text-slate-500">{et.duration_hours >= 1 ? `${et.duration_hours}h` : `${Math.round(et.duration_hours * 60)}m`} | Starts {fmtTime12(et.default_start_time)}{et.description ? ` | ${et.description}` : ""}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => { setEditingType(et); setTypeForm({ ...DEFAULT_TYPE_FORM, ...et }); }} data-testid={`edit-type-${et.id}`}>
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteType(et.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Add/Edit Type Form */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">{editingType ? "Edit Event Type" : "Add Event Type"}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Name *</Label>
                    <Input value={typeForm.name} onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })} placeholder="e.g., Roof Visit" className="h-10" data-testid="event-type-name" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Duration *</Label>
                    <select
                      value={typeForm.duration_hours}
                      onChange={(e) => setTypeForm({ ...typeForm, duration_hours: parseFloat(e.target.value) })}
                      className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F4C81]/20"
                      data-testid="event-type-duration"
                    >
                      <option value={0.25}>15 min</option>
                      <option value={0.5}>30 min</option>
                      <option value={0.75}>45 min</option>
                      <option value={1}>1 hour</option>
                      <option value={1.5}>1.5 hours</option>
                      <option value={2}>2 hours</option>
                      <option value={3}>3 hours</option>
                      <option value={4}>4 hours</option>
                      <option value={6}>6 hours</option>
                      <option value={8}>Full day (8h)</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Default Start Time</Label>
                    <TimePicker12 value={typeForm.default_start_time} onChange={(v) => setTypeForm({ ...typeForm, default_start_time: v })} testIdPrefix="type-start" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Description</Label>
                    <Input value={typeForm.description} onChange={(e) => setTypeForm({ ...typeForm, description: e.target.value })} placeholder="Brief description" className="h-10" />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Color</Label>
                  <div className="flex gap-2">
                    {DEFAULT_COLORS.map(c => (
                      <button key={c} type="button" className={`w-6 h-6 rounded-full border-2 transition-all ${typeForm.color === c ? "border-slate-800 scale-110" : "border-transparent"}`} style={{ backgroundColor: c }} onClick={() => setTypeForm({ ...typeForm, color: c })} />
                    ))}
                  </div>
                </div>

                {/* Install day toggle — flips the JB/JS automated messaging.
                    ON = reroof / fence install / landscape install (all-day
                    crew day, evening-before "crew rolls in 7-8am" text).
                    OFF = short visit (uses scheduled time in the reminder). */}
                <div className="flex items-start gap-3 rounded-md border border-slate-200 bg-slate-50 p-3">
                  <input
                    type="checkbox"
                    id="is-install-event"
                    checked={!!typeForm.is_install_event}
                    onChange={(e) => setTypeForm({ ...typeForm, is_install_event: e.target.checked })}
                    className="mt-1 h-4 w-4"
                    data-testid="event-type-install-toggle"
                  />
                  <div className="flex-1">
                    <label htmlFor="is-install-event" className="text-sm font-medium text-slate-800 cursor-pointer">
                      Install event (all-day crew day)
                    </label>
                    <p className="text-xs text-slate-500 leading-snug mt-0.5">
                      Turn ON for Reroof, Fence Install, Landscape Install. Customer gets a "crew rolls in between 7 and 8 tomorrow morning" text the evening before, plus a "you're scheduled for {"{date}"}" confirmation the moment you book. Leave OFF for consultations and short repairs.
                    </p>
                  </div>
                </div>

                {/* Auto project transitions */}
                <div className="pt-3 border-t border-slate-200 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-slate-700">Auto-Categorize Project</p>
                    <span className="text-[10px] text-slate-400">When this event is created</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-[11px]">Set service to</Label>
                      <select
                        value={typeForm.target_service_type || ""}
                        onChange={(e) => setTypeForm({ ...typeForm, target_service_type: e.target.value })}
                        className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-xs"
                        data-testid="target-service-select"
                      >
                        <option value="">— No change —</option>
                        {pipelineCategories
                          .filter(c => !["production", "billing"].includes(c.value))
                          .map(c => (
                            <option key={c.value} value={c.value}>{c.name}</option>
                          ))}
                      </select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[11px]">Move to stage</Label>
                      <select
                        value={typeForm.target_stage || ""}
                        onChange={(e) => setTypeForm({ ...typeForm, target_stage: e.target.value })}
                        className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-xs"
                        data-testid="target-stage-select"
                      >
                        <option value="">— No change —</option>
                        {pipelineStages
                          .filter(s => !typeForm.target_service_type || s.category === typeForm.target_service_type)
                          .sort((a, b) => (a.order || 0) - (b.order || 0))
                          .map(s => (
                            <option key={s.value} value={s.value}>{s.name}</option>
                          ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Customer email notifications */}
                <div className="pt-3 border-t border-slate-200 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-slate-700">Customer Email Notifications</p>
                    <span className="text-[10px] text-slate-400">Editable per event type</span>
                  </div>

                  {/* On create toggle */}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!typeForm.notify_on_create}
                      onChange={(e) => setTypeForm({ ...typeForm, notify_on_create: e.target.checked })}
                      className="h-4 w-4 rounded border-slate-300 text-[#0F4C81] focus:ring-[#0F4C81]"
                      data-testid="notify-on-create-toggle"
                    />
                    <span className="text-xs text-slate-700">Send confirmation email when event is created</span>
                  </label>

                  {typeForm.notify_on_create && (
                    <div className="pl-6 space-y-2">
                      <div className="space-y-1">
                        <Label className="text-[11px]">Subject</Label>
                        <Input
                          value={typeForm.email_subject_create || ""}
                          onChange={(e) => setTypeForm({ ...typeForm, email_subject_create: e.target.value })}
                          placeholder={DEFAULT_CREATE_SUBJECT}
                          className="h-9 text-xs"
                          data-testid="email-subject-create"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[11px]">Body (HTML)</Label>
                        <Textarea
                          value={typeForm.email_body_create || ""}
                          onChange={(e) => setTypeForm({ ...typeForm, email_body_create: e.target.value })}
                          placeholder={DEFAULT_CREATE_BODY}
                          rows={5}
                          className="text-xs font-mono"
                          data-testid="email-body-create"
                        />
                      </div>
                    </div>
                  )}

                  {/* Reminder toggle */}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!typeForm.notify_reminder}
                      onChange={(e) => setTypeForm({ ...typeForm, notify_reminder: e.target.checked })}
                      className="h-4 w-4 rounded border-slate-300 text-[#0F4C81] focus:ring-[#0F4C81]"
                      data-testid="notify-reminder-toggle"
                    />
                    <span className="text-xs text-slate-700">Send reminder email before event</span>
                  </label>

                  {typeForm.notify_reminder && (
                    <div className="pl-6 space-y-2">
                      <div className="space-y-1">
                        <Label className="text-[11px]">Send reminder</Label>
                        <select
                          value={typeForm.reminder_minutes_before || 120}
                          onChange={(e) => setTypeForm({ ...typeForm, reminder_minutes_before: parseInt(e.target.value, 10) })}
                          className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-xs focus:outline-none focus:ring-2 focus:ring-[#0F4C81]/20"
                          data-testid="reminder-minutes-before"
                        >
                          <option value={30}>30 minutes before</option>
                          <option value={60}>1 hour before</option>
                          <option value={120}>2 hours before</option>
                          <option value={240}>4 hours before</option>
                          <option value={1440}>1 day before</option>
                          <option value={2880}>2 days before</option>
                        </select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[11px]">Subject</Label>
                        <Input
                          value={typeForm.email_subject_reminder || ""}
                          onChange={(e) => setTypeForm({ ...typeForm, email_subject_reminder: e.target.value })}
                          placeholder={DEFAULT_REMINDER_SUBJECT}
                          className="h-9 text-xs"
                          data-testid="email-subject-reminder"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[11px]">Body (HTML)</Label>
                        <Textarea
                          value={typeForm.email_body_reminder || ""}
                          onChange={(e) => setTypeForm({ ...typeForm, email_body_reminder: e.target.value })}
                          placeholder={DEFAULT_REMINDER_BODY}
                          rows={5}
                          className="text-xs font-mono"
                          data-testid="email-body-reminder"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs"
                        disabled={sendingTest === "reminder"}
                        onClick={() => handleSendTestEmail("reminder")}
                        data-testid="send-test-reminder-btn"
                      >
                        {sendingTest === "reminder"
                          ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Sending…</>
                          : <><Send className="w-3 h-3 mr-1" /> Send test to me</>}
                      </Button>
                    </div>
                  )}

                  <p className="text-[10px] text-slate-400 leading-relaxed">
                    Variables: <code>{"{{customer_name}}"}</code>, <code>{"{{event_title}}"}</code>, <code>{"{{event_date}}"}</code>, <code>{"{{event_time}}"}</code>, <code>{"{{project_address}}"}</code>, <code>{"{{company_name}}"}</code>, <code>{"{{company_phone}}"}</code>,{" "}
                    <code className="text-[#0F4C81]">{"{{add_to_calendar_button}}"}</code> <span className="text-slate-500">(renders &quot;📅 Add to my calendar&quot; button)</span>, <code>{"{{ical_link}}"}</code> <span className="text-slate-500">(raw .ics URL)</span>
                  </p>
                </div>

                {/* Internal crew dispatch */}
                <div className="pt-3 border-t border-slate-200 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-slate-700">Crew Dispatch Email</p>
                    <span className="text-[10px] text-slate-400">Internal — to your team</span>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!typeForm.notify_crew_on_create}
                      onChange={(e) => setTypeForm({ ...typeForm, notify_crew_on_create: e.target.checked })}
                      className="h-4 w-4 rounded border-slate-300 text-[#0F4C81]"
                      data-testid="notify-crew-toggle"
                    />
                    <span className="text-xs text-slate-700">Email the crew when this event is booked</span>
                  </label>
                  {typeForm.notify_crew_on_create && (
                    <div className="pl-6 space-y-2">
                      <div className="space-y-1">
                        <Label className="text-[11px]">Recipient(s) — comma separated</Label>
                        <Input
                          value={typeForm.crew_email || ""}
                          onChange={(e) => setTypeForm({ ...typeForm, crew_email: e.target.value })}
                          placeholder="dispatch@company.com, crew-a@company.com"
                          className="h-9 text-xs"
                          data-testid="crew-email-recipients"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[11px]">Subject</Label>
                        <Input
                          value={typeForm.crew_email_subject || ""}
                          onChange={(e) => setTypeForm({ ...typeForm, crew_email_subject: e.target.value })}
                          placeholder="[Crew] {{event_title}} — {{event_date}}"
                          className="h-9 text-xs"
                          data-testid="crew-email-subject"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[11px]">Body (HTML) — supports {"{{customer_phone}}"}, {"{{crew}}"}, {"{{maps_link}}"}</Label>
                        <Textarea
                          value={typeForm.crew_email_body || ""}
                          onChange={(e) => setTypeForm({ ...typeForm, crew_email_body: e.target.value })}
                          placeholder="(default crew template will be used if blank)"
                          rows={5}
                          className="text-xs font-mono"
                          data-testid="crew-email-body"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs"
                        disabled={sendingTest === "crew"}
                        onClick={() => handleSendTestEmail("crew")}
                        data-testid="send-test-crew-btn"
                      >
                        {sendingTest === "crew"
                          ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Sending…</>
                          : <><Send className="w-3 h-3 mr-1" /> Send test to me</>}
                      </Button>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 pt-2">
                  {editingType && (
                    <Button variant="outline" size="sm" onClick={() => { setEditingType(null); setTypeForm({ ...DEFAULT_TYPE_FORM }); }}>Cancel</Button>
                  )}
                  <Button size="sm" className="bg-[#0F4C81] hover:bg-[#0D4270]" onClick={handleSaveType} data-testid="save-event-type-btn">
                    {editingType ? "Update Type" : "Add Type"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
